import _ from 'underscore';
import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
} from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { MODE } from './model/constants';
import { BudgetSuggestionOfflineContainer } from './components/budget-suggestion-offline-container';
import { useBudgetSuggestionRepository } from './use-budget-suggestion-repository';

const getStyles = () => ({
  editBudgetSuggestionButton: {
    color: 'rgb(0, 120, 212) !important',
    border: 'none !important',
    padding: '0px !important',
    fontWeight: '400!important',
    '&:active, &:focus, &:hover': {
      color: 'rgb(0, 120, 212) !important',
      backgroundColor: '#ffffff !important',
    },
  },
  learnMoreContent: {
    '& a.HelpTopic:focus': {
      outline: 'none !important',
    },
  },

});

export const BudgetSuggestionDialog = ({
  onBudgetChange,
  i18n,
  campaignId,
  campaignType,
  currency,
  enableSaving,
  campaign,
  onGridRefresh,
  showDialog,
  onTriggerDialog,
  minBudget,
  maxBudget,
  defaultBudget,
}) => {
  const classes = useDefaultStyles(getStyles);
  const [isOpen, setIsOpen] = useState(false);
  const [customBudgetResult, setCustomBudgetResult] = useState({ data: { budget: 20 }, isValid: true, messages: [] });

  const { loadBudget, fetchCustomBudgetPerformanceData, saveBudget } = useBudgetSuggestionRepository({
    campaignId,
    campaignType,
    currency,
    campaign,
  });

  const [isLoading, setIsLoading] = useState(true);

  const [budgetSuggestionData, setBudgetSuggestionData] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    loadBudget().then((data) => {
      setBudgetSuggestionData(data);
    }).finally(() => {
      setIsLoading(false);
    });
  }, [loadBudget]);

  const triggerDialog = useCallback((isDialogOpen) => {
    if (onTriggerDialog) {
      onTriggerDialog(isDialogOpen);
    } else {
      setIsOpen(isDialogOpen);
    }
  }, [onTriggerDialog]);

  const onChange = useCallback(() => {
    onBudgetChange({ Amount: customBudgetResult.data.budget, Type: 'DailyStandard' }, customBudgetResult.isValid);
    triggerDialog(false);
  }, [customBudgetResult, onBudgetChange, triggerDialog]);

  return (
    <div className="budget-suggestion-dialog-container">
      {
        !onTriggerDialog && (
          <Button
            className={classNames(classes.editBudgetSuggestionButton, 'edit-budget-suggestion-button')}
            onClick={() => triggerDialog(true)}
          >
            { i18n.getString(_TL_('View budget recommendations'))}
          </Button>
        )
      }
      <Dialog
        modalType="modal"
        open={showDialog || isOpen}
        className={classNames(classes.dialog, 'budget-suggestion-dialog')}
      >
        <DialogSurface className="budget-suggestion-dialog">
          <DialogBody>
            <DialogTitle >
              {i18n.getString(_TL_('Budget recommendations'))}
            </DialogTitle>
            <DialogContent>
              <div className={classNames(classes.learnMoreContent)}>
                {i18n.getString(_TL_('Your ads could be shown more often. You can increase your budget to ensure more ad exposure.'))}
                <LearnMoreLink
                  linkText={i18n.getString(_TL_('Learn more about changing a limited or out of budget delivery status.'))}
                  topic="app51009"
                />
              </div>
              <BudgetSuggestionOfflineContainer
                data={budgetSuggestionData}
                isLoading={isLoading}
                mode={MODE.EDITING}
                currency={currency}
                campaignType={campaignType}
                onBudgetValidate={setCustomBudgetResult}
                fetchCustomBudgetPerformanceData={fetchCustomBudgetPerformanceData}
                bidType={campaign && campaign.BiddingScheme && campaign.BiddingScheme.Type}
                minBudget={minBudget}
                maxBudget={maxBudget}
                defaultBudget={defaultBudget}
              />
            </DialogContent>
            <DialogActions position="start" className={classes.buttonGroup}>
              <Button
                className={classNames(classes.applyButton, 'apply-button')}
                appearance="primary"
                onClick={() => {
                  if (enableSaving) {
                    saveBudget({
                       campaign: { campaign },
                       budgetAmount: `${customBudgetResult.data.budget}`,
                       budgetTier: customBudgetResult.data.budgetTier,
                      }).then(() => {
                        onGridRefresh();
                      });
                    triggerDialog(false);
                  } else {
                    onChange();
                  }
                }
                }
              >
                {i18n.getString(_TL_('Apply'))}
              </Button>
              <Button className="cancel-button" onClick={() => triggerDialog(false)} appearance="secondary" >{i18n.getString(_TL_('Cancel'))} </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

BudgetSuggestionDialog.propTypes = {
  budget: PropTypes.shape({
    Amount: PropTypes.number,
  }).isRequired,
  onBudgetChange: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  campaignId: PropTypes.string.isRequired,
  campaignType: PropTypes.string,
  currency: PropTypes.string,
  enableSaving: PropTypes.bool,
  onGridRefresh: PropTypes.func,
  campaign: PropTypes.shape({}),
  showDialog: PropTypes.bool,
  onTriggerDialog: PropTypes.func,
};

BudgetSuggestionDialog.defaultProps = {
  campaignType: 'performanceMax',
  currency: 'USD',
  enableSaving: false,
  onGridRefresh: _.noop,
  campaign: null,
  showDialog: false,
  onTriggerDialog: null,
};
